import { TFunction } from 'i18next';

import { DebugStepType } from '@/shared/types/tryIt';

import { ConversationTurn } from '../../../types/conversation';
import { flattenSteps } from '../../../utils/steps';

export type TurnHighlight = { key: string; text: string; nodeId: string; dialogName: string };

export type TurnDialog = { id: string; name: string };

type DialogOfNode = (nodeId?: string) => TurnDialog | null;

const isJump = (step: { type: DebugStepType; action_type?: string }) =>
  step.type === DebugStepType.ACTION && step.action_type === 'event';

export const buildTurnHighlights = (
  t: TFunction,
  turn: ConversationTurn,
  dialogOfNode: DialogOfNode
): TurnHighlight[] => {
  const steps = flattenSteps(turn.metadata.steps);
  // Only nodes before the first jump say where the turn started; a later one is a destination.
  const firstJump = steps.findIndex(isJump);
  const opening = (firstJump === -1 ? steps : steps.slice(0, firstJump)).find(
    (step) => step.node_id && !isJump(step)
  );
  let current = dialogOfNode(opening?.node_id);
  const visited = new Set(current ? [current.id] : []);

  return steps.flatMap((step, index) => {
    if (!isJump(step) || !step.node_id) {
      return [];
    }

    const dialog = dialogOfNode(step.node_id);
    if (!dialog || dialog.id === current?.id) {
      return [];
    }

    const text = visited.has(dialog.id)
      ? t('try_it.turn_highlights.returned_to_dialog')
      : t('try_it.turn_highlights.moved_to_dialog');
    visited.add(dialog.id);
    current = dialog;

    return [{ key: `step-${index}`, text, nodeId: step.node_id, dialogName: dialog.name }];
  });
};
